import React, { useState } from 'react';
import { Eye, Edit2, Trash2 } from 'lucide-react';
import { Pagination } from './Pagination';

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
};

const ITEMS_PER_PAGE = 10;

export const TransactionTable = ({ transactions = [], onEdit, onView, onDelete }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">No transactions found</p>
      </div>
    );
  }

  const totalPages = Math.ceil(transactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTransactions = transactions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <>
      {/* Desktop View */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm text-gray-700 dark:text-gray-300">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <tr>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-900 dark:text-gray-200 text-xs sm:text-sm">Date</th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-900 dark:text-gray-200 text-xs sm:text-sm">Description</th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-900 dark:text-gray-200 text-xs sm:text-sm">Category</th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-900 dark:text-gray-200 text-xs sm:text-sm">Amount</th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-900 dark:text-gray-200 text-xs sm:text-sm">Type</th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-900 dark:text-gray-200 text-xs sm:text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.map((tx) => (
              <tr key={tx.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm whitespace-nowrap">{formatDate(tx.created_at)}</td>
                <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm truncate max-w-xs">{tx.description}</td>
                <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm">{tx.category || 'N/A'}</td>
                <td className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold text-xs sm:text-sm whitespace-nowrap ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                  {tx.type === 'income' ? '+' : '-'}₦{tx.amount?.toLocaleString() || '0'}
                </td>
                <td className="px-3 sm:px-6 py-2 sm:py-3 capitalize text-xs sm:text-sm">{tx.type}</td>
                <td className="px-3 sm:px-6 py-2 sm:py-3">
                  <div className="flex gap-1">
                    {onView && (
                      <button
                        onClick={() => onView(tx.id)}
                        className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(tx.id)}
                        className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(tx.id)}
                        className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View - Card Layout */}
      <div className="sm:hidden space-y-3">
        {paginatedTransactions.map((tx) => (
          <div key={tx.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <p className="text-xs text-gray-600 dark:text-gray-400">{new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{tx.description}</p>
              </div>
              <span className={`text-sm font-bold whitespace-nowrap ml-2 ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                {tx.type === 'income' ? '+' : '-'}₦{tx.amount?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded font-semibold">
                {tx.category || 'N/A'}
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">{tx.type}</span>
            </div>
            <div className="flex gap-2 border-t dark:border-gray-600 pt-2">
              {onView && (
                <button
                  onClick={() => onView(tx.id)}
                  className="flex-1 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded text-xs sm:text-sm flex items-center justify-center gap-1 transition font-semibold"
                  title="View"
                >
                  <Eye className="w-3.5 h-3.5" /> View
                </button>
              )}
              {onEdit && (
                <button
                  onClick={() => onEdit(tx.id)}
                  className="flex-1 p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded text-xs sm:text-sm flex items-center justify-center gap-1 transition font-semibold"
                  title="Edit"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(tx.id)}
                  className="flex-1 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-xs sm:text-sm flex items-center justify-center gap-1 transition font-semibold"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          onPageChange={setCurrentPage}
        />
      )}
    </>
  );
};
